// src/components/Dashboard/ClaimNotes.jsx
// Módulo 5 del spec del cliente: "Notes Log" — notas auditables por
// reclamo, con ventana de gracia de NOTE_EDIT_WINDOW_HOURS (48h) para
// editar/borrar; pasado ese tiempo la nota queda de solo lectura. Agregar
// nota: admin o el ajustador dueño/con viewAllClaims de este reclamo
// (`canWrite`, mismo criterio que `canEditClaim` en ClaimDetailPage.jsx —
// ampliado 2026-09-20 para que el P.A. pueda dejar notas, que quedan
// respaldadas en "Casos y notas" del admin vía subscribeAllClaimNotes).
// Editar/eliminar sigue siendo admin-only: una nota es un registro
// auditable, no algo que el autor deba poder borrar después.
import React, { useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import ConfirmModal from '../common/ConfirmModal';
import { LanguageContext } from '../../App';
import { useAuth } from '../../contexts/AuthContext';
import {
  getClaimNotes,
  createClaimNote,
  updateClaimNote,
  deleteClaimNote,
  isNoteEditable,
  NOTE_EDIT_WINDOW_HOURS
} from '../../services/claimNoteService';

const formatDateTime = (note) => {
  const ms = note.createdAt?.toMillis?.();
  if (!ms) return '';
  return new Date(ms).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

const ClaimNotes = ({ claimRecordId, canWrite = false, claimMeta = {}, embedded = false, showComposer = true, onComposerClose }) => {
  const { language } = useContext(LanguageContext);
  const { isAdmin } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const load = async () => {
    if (!claimRecordId) return;
    setLoading(true);
    try {
      setNotes(await getClaimNotes(claimRecordId));
    } catch (err) {
      toast.error(language === 'es' ? 'No se pudieron cargar las notas.' : 'Could not load notes.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimRecordId]);

  const handleAdd = async (e) => {
    e.preventDefault();
    const text = newNote.trim();
    if (!text) return;
    setSaving(true);
    try {
      const note = await createClaimNote(claimRecordId, text, claimMeta);
      setNotes(current => [note, ...current]);
      setNewNote('');
      onComposerClose?.();
    } catch (err) {
      toast.error(language === 'es' ? 'No se pudo guardar la nota.' : 'Could not save the note.');
    } finally {
      setSaving(false);
    }
  };

  const handleStartEdit = (note) => {
    setEditingId(note.id);
    setEditDraft(note.text);
  };

  const handleSaveEdit = async (noteId) => {
    const text = editDraft.trim();
    if (!text) return;
    setSavingEdit(true);
    try {
      await updateClaimNote(noteId, text);
      setNotes(current => current.map(n => n.id === noteId ? { ...n, text, edited: true } : n));
      setEditingId(null);
      toast.success(language === 'es' ? 'Nota actualizada.' : 'Note updated.');
    } catch (err) {
      toast.error(language === 'es' ? 'No se pudo editar la nota (¿venció el plazo de 48h?).' : 'Could not edit the note (did the 48h window pass?).');
    } finally {
      setSavingEdit(false);
    }
  };

  // Confirmación con el diálogo de la app (antes window.confirm).
  const [pendingDelete, setPendingDelete] = useState(null);
  const handleDelete = note => setPendingDelete(note);
  const confirmDelete = async () => {
    const note = pendingDelete;
    setPendingDelete(null);
    try {
      await deleteClaimNote(note.id);
      setNotes(current => current.filter(n => n.id !== note.id));
      toast.success(language === 'es' ? 'Nota eliminada.' : 'Note deleted.');
    } catch (err) {
      toast.error(language === 'es' ? 'No se pudo eliminar la nota (¿venció el plazo de 48h?).' : 'Could not delete the note (did the 48h window pass?).');
    }
  };

  if (!claimRecordId) return null;

  return (
    <div className={embedded ? 'claim-notes-embedded' : 'cd-attach-section'}>
      {!embedded && <div className="cd-detail-checks-header">
        <h2 className="cd-detail-checks-title">{language === 'es' ? 'Notas del reclamo' : 'Claim notes'}</h2>
        <span className="cd-detail-checks-count">
          {language === 'es' ? `Editable ${NOTE_EDIT_WINDOW_HOURS}h` : `Editable ${NOTE_EDIT_WINDOW_HOURS}h`}
        </span>
      </div>}

      <div className="cd-attach-explorer">
        {canWrite && showComposer && (
          <form className={embedded ? 'claim-case-form' : 'cd-notes-new'} onSubmit={handleAdd}>
            <label>{language === 'es' ? 'Nota' : 'Note'}<textarea
              className="cd-notes-textarea"
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder={language === 'es' ? 'Escribe una nota sobre este reclamo...' : 'Write a note about this claim...'}
              rows={2}
              disabled={saving}
            /></label>
            <div className="cd-note-edit-actions">
            <button type="submit" className="cd-btn cd-btn-check" disabled={saving || !newNote.trim()}>
              {saving ? (language === 'es' ? 'Guardando…' : 'Saving…') : (language === 'es' ? 'Guardar nota' : 'Save note')}
            </button>
            {embedded && <button type="button" className="cd-attach-mini-btn" disabled={saving} onClick={onComposerClose}>{language === 'es' ? 'Cancelar' : 'Cancel'}</button>}
            </div>
          </form>
        )}

        <div className="cd-notes-list">
          {loading && <p className="cd-attach-empty">{language === 'es' ? 'Cargando notas...' : 'Loading notes...'}</p>}
          {!loading && notes.length === 0 && (
            <p className="cd-attach-empty">{language === 'es' ? 'Todavía no hay notas.' : 'No notes yet.'}</p>
          )}
          {!loading && notes.map(note => {
            const editable = isAdmin && isNoteEditable(note);
            const isEditing = editingId === note.id;
            return (
              <div key={note.id} className="cd-note-card">
                <div className="cd-note-head">
                  <span className="cd-note-author">{note.createdByName || note.createdByEmail}</span>
                  <span className="cd-note-date">
                    {formatDateTime(note)}
                    {note.edited && <em className="cd-note-edited"> · {language === 'es' ? 'editada' : 'edited'}</em>}
                    {!editable && <span className="cd-note-locked" title={language === 'es' ? 'Solo lectura: pasaron 48h' : 'Read-only: 48h passed'}><i className="fa-solid fa-lock"></i></span>}
                  </span>
                </div>
                {isEditing ? (
                  <div className="cd-note-edit">
                    <textarea className="cd-notes-textarea" value={editDraft} onChange={(e) => setEditDraft(e.target.value)} rows={2} disabled={savingEdit} />
                    <div className="cd-note-edit-actions">
                      <button type="button" className="cd-btn cd-btn-check" onClick={() => handleSaveEdit(note.id)} disabled={savingEdit || !editDraft.trim()}>
                        <i className="fa-solid fa-check"></i> {language === 'es' ? 'Guardar' : 'Save'}
                      </button>
                      <button type="button" className="cd-attach-mini-btn" onClick={() => setEditingId(null)} disabled={savingEdit}>
                        <i className="fa-solid fa-xmark"></i>
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="cd-note-text">{note.text}</p>
                )}
                {editable && !isEditing && (
                  <div className="cd-note-actions">
                    <button type="button" className="cd-attach-mini-btn" onClick={() => handleStartEdit(note)} title={language === 'es' ? 'Editar' : 'Edit'}>
                      <i className="fa-solid fa-pen"></i>
                    </button>
                    <button type="button" className="cd-attach-mini-btn cd-attach-mini-danger" onClick={() => handleDelete(note)} title={language === 'es' ? 'Eliminar' : 'Delete'}>
                      <i className="fa-solid fa-trash"></i>
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <ConfirmModal
        open={!!pendingDelete}
        icon="fa-trash"
        danger
        title={language === 'es' ? '¿Eliminar esta nota?' : 'Delete this note?'}
        message={language === 'es' ? 'No se puede deshacer.' : 'This cannot be undone.'}
        confirmLabel={language === 'es' ? 'Eliminar' : 'Delete'}
        cancelLabel={language === 'es' ? 'Cancelar' : 'Cancel'}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
};

export default ClaimNotes;
